import {useNavigate} from 'react-router-dom';

import {TypeLocalNameEnum} from 'domain/usecases';
import {ExamBookingEnum} from 'presentation/constants';
import {usePersonalData} from 'presentation/hooks';
import {BookingOriginEnum} from 'presentation/modules/booking/shared/constants/bookingOriginEnum';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useUnitsListStore} from 'presentation/modules/booking/shared/store/useUnitsListStore';
import {getExamStepPosition} from 'presentation/modules/booking/exam/utils/getExamStepPosition';
import {BookingTypeEnum} from 'presentation/pages/private/booking/constants';
import {translations} from 'presentation/translations-new';

import {ADVANCE_DIAGNOSTICS_UNIT} from '../constants';
import {RebookAdvanceDiagnosticsExamParams} from './types';

const {unitName, unitAddress} =
	translations['pt-br'].privateFlow.home.advanceDiagnosticsLocationModal;

function useAdvanceDiagnosticsExamBooking() {
	const navigate = useNavigate();
	const resetBookingData = useBookingStore(state => state.resetBookingData);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const resetUnitsList = useUnitsListStore(state => state.resetUnitsList);
	const {currentEmail, currentPhone} = usePersonalData();

	const {SPECIALITY_OR_EXAM_STEP, PROFESSIONAL_STEP} = getExamStepPosition({
		isManualLocation: true,
	});

	const setupAdvanceDiagnosticsBookingData = ({
		email,
		phone,
		...rebookingData
	}: Partial<RebookAdvanceDiagnosticsExamParams> = {}) => {
		resetUnitsList();
		resetBookingData({
			bookingType: BookingTypeEnum.EXAM,
			bookingOrigin: BookingOriginEnum.BOOKING_EXAM,
			email: email || currentEmail,
			phone: phone || currentPhone,
			ufCode: ADVANCE_DIAGNOSTICS_UNIT.stateCode,
			ufName: ADVANCE_DIAGNOSTICS_UNIT.stateName,
			cityName: ADVANCE_DIAGNOSTICS_UNIT.cityName,
			typeLocalName: TypeLocalNameEnum.CIDADE,
			districtName: ADVANCE_DIAGNOSTICS_UNIT.districtName,
			citiesNames: ADVANCE_DIAGNOSTICS_UNIT.citiesNames,
			unitCode: ADVANCE_DIAGNOSTICS_UNIT.unitCode,
			unitName,
			unitAddress,
			isManualLocation: true,
			isAdvanceDiagnosticsFlow: true,
			...rebookingData,
		});
	};

	const startAdvanceDiagnosticsExamBooking = () => {
		setupAdvanceDiagnosticsBookingData();
		setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);
		navigate(ExamBookingEnum.EXAM_BOOKING_FLOW);
	};

	const rebookAdvanceDiagnosticsExam = (params: RebookAdvanceDiagnosticsExamParams) => {
		setupAdvanceDiagnosticsBookingData({...params, isRebooking: true});
		setActiveBookingFlowStep(PROFESSIONAL_STEP);
		navigate(ExamBookingEnum.EXAM_BOOKING_FLOW, {replace: true});
	};

	return {startAdvanceDiagnosticsExamBooking, rebookAdvanceDiagnosticsExam};
}

export {useAdvanceDiagnosticsExamBooking};
